import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { useReviewerResources } from '../../../hooks/index.js';
import type { ReviewerResource } from '../../../types/project.js';

/** レビュー割合セルの共通クラス（数字は等幅 + 桁揃え）。 @graph-connects none */
const RATIO_BASE_CLASS = 'w-11 text-right font-medium tabular-nums';

/** しきい値を超えたレビュー割合のクラス（赤字）。 @graph-connects none */
export const RATIO_OVER_CLASS = `${RATIO_BASE_CLASS} text-[var(--color-error)]`;

/** しきい値以内のレビュー割合のクラス（通常色）。 @graph-connects none */
export const RATIO_NORMAL_CLASS = `${RATIO_BASE_CLASS} text-[var(--color-text-primary)]`;

/** レビュー割合の表示（算出できない場合はダッシュ）。 @graph-connects none */
function formatRatio(ratio: number | null): string {
  return ratio === null ? '—' : `${String(ratio)}%`;
}

/**
 * レビュアー 1 行。ニックネームと数値は等幅フォントで揃え、割合はしきい値超過なら赤で表示する。
 * 区切り線は行ごとの下ボーダーで引く（2 列グリッドでは divide-y が列跨ぎで崩れるため）。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business レビュアー1名分の行（ニックネーム・担当レビュー案件数・レビュー割合）を描画し、しきい値超過を赤字で強調する
 * @graph-connects none
 */
function ReviewerRow({
  reviewer,
  warnThresholdPercent,
}: {
  reviewer: ReviewerResource;
  warnThresholdPercent: number;
}) {
  const { t } = useTranslation();
  const isOver =
    reviewer.reviewRatioPercent !== null && reviewer.reviewRatioPercent > warnThresholdPercent;

  return (
    <div
      className="flex items-baseline justify-between gap-2 border-b border-[var(--color-border)] py-1 font-mono text-xs"
      title={`${String(reviewer.plannedReviewPoint)}pt / ${String(reviewer.capacity)}pt`}
    >
      <span className="truncate text-[var(--color-text-secondary)]">{reviewer.nickname}</span>
      <span className="flex shrink-0 items-baseline gap-2">
        <span className="tabular-nums text-[var(--color-text-muted)]">
          {t('projects.reviewerResource.projectCount', { value: reviewer.projectCount })}
        </span>
        <span className={isOver ? RATIO_OVER_CLASS : RATIO_NORMAL_CLASS}>
          {formatRatio(reviewer.reviewRatioPercent)}
        </span>
      </span>
    </div>
  );
}

/**
 * レビュアーリソースカード（/projects 上部サマリーの中央カード）。
 *
 * 現在コードレビュー（Backlog 種別「レビュー」）を担当しているエンジニアを
 * ニックネーム昇順で並べ、担当レビュー案件数とレビュー割合を表示する。
 * レビュー割合 = 未完了レビュー子チケットの予定pt 合計 / 今日から 1 ヶ月先までの capacity。
 * 人数が多く 1 列では収まらないため、sm 以上では 2 列に折り返して compact に並べる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business プロジェクト管理ページ上部の「レビュアーリソース」カード。レビュアー別の担当案件数とレビュー割合を2列で一覧表示し、しきい値超過を赤字で警告する
 * @graph-connects useReviewerResources [calls] レビュアーリソース取得フック呼び出し
 */
export function ReviewerResourceCard() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useReviewerResources();

  return (
    <Card className="animate-content-reveal h-full flex flex-col">
      <CardContent className="flex flex-1 flex-col">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('projects.reviewerResource.title')}
            {data && data.data.length > 0 && (
              <span className="ml-2 font-mono text-xs tabular-nums text-[var(--color-text-muted)]">
                {t('projects.reviewerResource.memberCount', { value: data.totalCount })}
              </span>
            )}
          </p>
          {/* 右列が何の数字かのラベルと、赤字の判定基準（しきい値）の凡例。 */}
          <p className="flex shrink-0 items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
            <span>{t('projects.reviewerResource.ratioLabel')}</span>
            <span className="inline-block size-1.5 rounded-full bg-[var(--color-error)]" />
            <span className="font-mono tabular-nums">
              {t('projects.reviewerResource.warnLegend', {
                value: data?.warnThresholdPercent ?? 0,
              })}
            </span>
          </p>
        </div>

        {isLoading ? (
          <p className="py-6 text-center text-sm text-[var(--color-text-muted)]">
            {t('projects.reviewerResource.loading')}
          </p>
        ) : error ? (
          <p className="py-6 text-center text-sm text-[var(--color-text-muted)]">
            {t('projects.reviewerResource.error')}
          </p>
        ) : !data || data.data.length === 0 ? (
          <p className="py-6 text-center text-sm text-[var(--color-text-muted)]">
            {t('projects.reviewerResource.empty')}
          </p>
        ) : (
          <div className="mt-3 grid max-h-48 grid-cols-1 gap-x-6 overflow-y-auto pr-1 sm:grid-cols-2">
            {data.data.map((reviewer) => (
              <ReviewerRow
                key={reviewer.email}
                reviewer={reviewer}
                warnThresholdPercent={data.warnThresholdPercent}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
